import i18n from '@/core/i18n';
import { ClientStatus } from '@/modules/clients/types';
import { BaseStatus, StatusConfig } from '@/modules/common';
import { createStatusOptionsMapper } from '@/modules/common/mappers/status-options.mapper';

/**
 * Client status classes that implement the BaseStatus interface.
 * Each status knows how to configure itself for the StatusBadge component.
 */
export class ClientStatusActive implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-green-600 text-green-600',
    };
  }
}

export class ClientStatusArchived implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-muted-foreground text-muted-foreground',
    };
  }
}

export class ClientStatusUnknown implements BaseStatus {
  getConfig(): StatusConfig {
    return {
      colorClasses: 'border border-muted-foreground text-muted-foreground',
    };
  }
}

// Factory function to create status instances based on enum values
export const createClientStatus = (status: string): BaseStatus => {
  switch (status) {
    case ClientStatus.Active:
      return new ClientStatusActive();
    case ClientStatus.Archived:
      return new ClientStatusArchived();
    default:
      return new ClientStatusUnknown(); // Default fallback
  }
};

/**
 * Get localized status text for client status.
 */
export const getClientStatusText = (status: ClientStatus): string => {
  switch (status) {
    case ClientStatus.Active:
      return i18n.t('authenticated.clients:status.active');
    case ClientStatus.Archived:
      return i18n.t('authenticated.clients:status.archived');
    default:
      return i18n.t('authenticated.clients:status.unknown');
  }
};

export const getClientStatusColor = (status: ClientStatus): string => {
  switch (status) {
    case ClientStatus.Active:
      return 'green-600';
    case ClientStatus.Archived:
      return 'muted-foreground';
    default:
      return 'muted-foreground';
  }
};

/**
 * Preconfigured mapper for client status options.
 * Maps an array of ClientStatus values to option objects with label, value, and color.
 */
export const getClientStatusOptions = createStatusOptionsMapper<ClientStatus>({
  getStatusColor: getClientStatusColor,
  getStatusText: getClientStatusText,
});
